<template>
  <stripe-loader class="card-form">
    <div v-if="intentLoaded">
      <!-- Pending Verification (requires_action / 3DS) -->
      <div class="card-form__inner payment-message text-center" v-if="requiresAction">
        <div>
          <p class="text-xl text-brand-black font-semibold mb-4 pt-10">{{ $t('form.requiresVerification') }}</p>
          <i18n v-if="pendingCard" path="form.cardEnding" tag="p" class="text-base text-brand-medium-brown mb-6">
            <template #brand>{{ pendingCard.brand | capitalize }}</template>
            <template #last4>{{ pendingCard.last4 }}</template>
          </i18n>
        </div>
        <button
          :class="['card-form__button', 'main-cta', { disabled: verificationInProgress }]"
          @click="completeVerification"
          type="button"
        >
          <span v-if="verificationInProgress"><Loader /> {{ $t('form.verifying') }}</span>
          <span v-else>{{ $t('form.completeVerification') }}</span>
        </button>
        <p class="mt-4">
          <a class="text-sm text-brand-medium-brown underline cursor-pointer" @click="switchToAlternatePayment">{{ $t('form.useDifferentMethod') }}</a>
        </p>
        <p class="mt-2">
          <a class="text-sm text-brand-medium-brown underline cursor-pointer" @click="redirect">{{ $t('form.continueTo', { company: companyName }) }}</a>
        </p>
      </div>
      <!-- Subscription invoice requires verification (3DS) -> in-app challenge -->
      <div class="card-form__inner payment-message text-center" v-else-if="invoiceRequiresVerification">
        <div>
          <p class="text-xl text-brand-black font-semibold mb-4 pt-10">{{ $t('form.requiresVerification') }}</p>
          <i18n v-if="invoiceCard" path="form.cardEnding" tag="p" class="text-base text-brand-medium-brown mb-6">
            <template #brand>{{ invoiceCard.brand | capitalize }}</template>
            <template #last4>{{ invoiceCard.last4 }}</template>
          </i18n>
        </div>
        <button class="card-form__button main-cta" :disabled="verificationInProgress" @click="verifyInvoicePayment" type="button">
          {{ verificationInProgress ? `${$t('form.verifying')}…` : $t('form.verifyCard') }}
        </button>
        <p class="mt-4">
          <a class="text-sm text-brand-medium-brown underline cursor-pointer" @click="switchToAlternatePayment">{{ $t('form.useDifferentMethod') }}</a>
        </p>
        <p class="mt-2">
          <a class="text-sm text-brand-medium-brown underline cursor-pointer" @click="redirect">{{ $t('form.continueTo', { company: companyName }) }}</a>
        </p>
      </div>
      <!-- Discount Banner -->
      <div class="card-form__banner text-center text-sm bg-orange-100 rounded-t-lg mx-30" v-if="showForm && bannerText !== null">
        {{ bannerText }}
      </div>
      <div class="card-form__inner justify-content content-center flex flex-wrap mx-10" v-if="showForm">
        <!-- STRIPE ELEMENTS -->
        <div class="payment-simple w-full">
          <StripeElements :stripe-key="pk_key" :instance-options="instanceOptions" :elements-options="elementsOptions" #default="{ elements }" ref="elms">
            <StripeElement type="payment" :elements="elements" @ready="isLoaded" ref="payment" class="md:w-11/12 md:mx-auto" />
          </StripeElements>
        </div>
        <button v-if="elementsLoaded" :class="['card-form__button', 'main-cta', { disabled: isUpdating }]" @click="updateCard">
          <span v-if="isUpdating"><Loader /> {{ $t('form.updating') }}</span>
          <span v-else>{{ updateButtonLabel }}</span>
        </button>
      </div>
      <!-- Update Failed Message -->
      <div class="card-form__inner payment-message text-center" v-if="cardUpdateFailed">
        <div>
          <p class="text-xl text-brand-black font-semibold mb-8">{{ message.header }}</p>
          <p class="text-lg text-brand-black mt-4">{{ message.description }}</p>
        </div>
        <button class="card-form__button mt-15 main-cta" @click="retry" type="button">{{ $t('form.tryAgain') }}</button>
      </div>
      <!-- Update Success Message -->
      <div class="card-form__inner payment-message text-center" v-if="cardUpdateSuccess">
        <div>
          <p class="text-xl text-brand-black font-semibold mb-8">{{ message.header }}</p>
          <p class="text-lg text-brand-black mt-4">{{ message.description }}</p>
        </div>
        <button class="card-form__button mt-15" @click="redirect" type="button">{{ $t('form.continueTo', { company: companyName }) }}</button>
      </div>
      <!-- Append watermark: IF cardUpdateSuccess & org has watermarks enabled -->
      <div v-if="showWatermark" class="mt-4">
        <Watermark :message="$t('watermark.subscriptionVerified')" product="pr" />
      </div>

      <!-- Payment is Already Complete (Invoice or PaymentIntent) -->
      <div class="card-form__inner payment-message text-center" v-if="alreadyPaid && !cardUpdateComplete">
        <div>
          <p class="text-xl text-brand-black font-semibold mb-8 pt-10">{{ isPaymentIntentBased ? $t('state.paymentPaid') : $t('state.invoicePaid') }}</p>
        </div>
        <button class="card-form__button mt-15 main-cta" @click="redirect" type="button">{{ $t('form.continueTo', { company: companyName }) }}</button>
      </div>
      <!-- PaymentIntent is Pending Capture (manual capture mode) -->
      <div class="card-form__inner payment-message text-center" v-else-if="isPendingCapture && !cardUpdateComplete">
        <div>
          <p class="text-xl text-brand-black font-semibold mb-8 pt-10">{{ $t('state.pendingCapture') }}</p>
        </div>
        <button class="card-form__button mt-15 main-cta" @click="redirect" type="button">{{ $t('form.continueTo', { company: companyName }) }}</button>
      </div>
      <!-- Invoice is Already Void (not applicable to PaymentIntents) -->
      <div class="card-form__inner payment-message text-center" v-else-if="alreadyVoid && !cardUpdateComplete">
        <div>
          <p class="text-xl text-brand-black font-semibold mb-8 pt-10">{{ $t('state.void') }}</p>
        </div>
        <button class="card-form__button mt-15 main-cta" @click="redirect" type="button">{{ $t('form.continueTo', { company: companyName }) }}</button>
      </div>
      <!-- Invoice is Already Uncollectible (not applicable to PaymentIntents) -->
      <div class="card-form__inner payment-message text-center" v-else-if="alreadyUncollectible && treatUncollectibleAsVoid && !cardUpdateComplete">
        <div>
          <p class="text-xl text-brand-black font-semibold mb-8 pt-10">{{ $t('state.uncollectible') }}</p>
        </div>
        <button class="card-form__button mt-15 main-cta" @click="redirect" type="button">{{ $t('form.continueTo', { company: companyName }) }}</button>
      </div>
      <!-- Invoice past collection period (not applicable to PaymentIntents) -->
      <div class="card-form__inner payment-message text-center" v-else-if="pastCollectionPeriod && !cardUpdateComplete">
        <div>
          <p class="text-xl text-brand-black font-semibold mb-8 pt-10">{{ $t('state.pastCollection') }}</p>
        </div>
        <button class="card-form__button mt-15 main-cta" @click="redirect" type="button">{{ $t('form.continueTo', { company: companyName }) }}</button>
      </div>
      <!-- PaymentIntent was canceled -->
      <div class="card-form__inner payment-message text-center" v-else-if="isCanceled && !cardUpdateComplete">
        <div>
          <p class="text-xl text-brand-black font-semibold mb-8 pt-10">{{ $t('state.canceled') }}</p>
        </div>
        <button class="card-form__button mt-15 main-cta" @click="redirect" type="button">{{ $t('form.continueTo', { company: companyName }) }}</button>
      </div>
      <!-- PaymentIntent is processing (e.g. bank debit settling) -->
      <div class="card-form__inner payment-message text-center" v-else-if="isProcessing && !cardUpdateComplete">
        <div>
          <p class="text-xl text-brand-black font-semibold mb-8 pt-10">{{ $t('state.processing') }}</p>
        </div>
        <button class="card-form__button mt-15 main-cta" @click="redirect" type="button">{{ $t('form.continueTo', { company: companyName }) }}</button>
      </div>
      <!-- PaymentIntent requires server-side confirmation -->
      <div class="card-form__inner payment-message text-center" v-else-if="isRequiresConfirmation && !cardUpdateComplete">
        <div>
          <p class="text-xl text-brand-black font-semibold mb-8 pt-10">{{ $t('state.requiresConfirmation') }}</p>
        </div>
        <button class="card-form__button mt-15 main-cta" @click="redirect" type="button">{{ $t('form.continueTo', { company: companyName }) }}</button>
      </div>
    </div>
  </stripe-loader>
</template>

<script>
import { StripeElements, StripeElement } from 'vue-stripe-elements-plus';
import StripeLoader from '@/components/StripeLoader.vue';
import Loader from '@/components/ButtonLoader.vue';
import Watermark from '@/components/Watermark.vue';

import appearance from '@/helpers/elements-config';
import * as Filters from '@/helpers/filters';
import { trackPageEvent } from '@/helpers/page-events';
import EventBus from '@/helpers/event-bus';
import OrgService from '@/api/org.service';
import router from '@/router';

export default {
  name: 'StripeForm',
  components: {
    StripeElements,
    StripeElement,
    StripeLoader,
    Loader,
    Watermark,
  },
  computed: {
    updateButtonLabel() {
      const { ctaText } = this.$store.state.account;
      if (ctaText) return ctaText;
      if (this.dunningPaymentIntent?.status === 'requires_action') return this.$t('form.verifyCard');
      return this.$t('form.updatePaymentMethod');
    },
    bannerText() {
      if (this.futureDiscount?.displayDiscount && this.invoiceDiscount?.displayDiscount) {
        return this.$t('banner.bothDiscounts');
      }
      if (this.futureDiscount?.displayDiscount) {
        const { coupon } = this.futureDiscount.displayDiscount;
        return this.$t('banner.futureDiscount', { amount: Filters.formatCouponAmount(coupon), duration: Filters.translateCouponDuration(coupon, 'for') });
      }
      if (this.invoiceDiscount?.displayDiscount) {
        const { coupon } = this.invoiceDiscount.displayDiscount;
        return this.$t('banner.invoiceDiscount', { amount: Filters.formatCouponAmount(coupon) });
      }
      return null;
    },
    isPaymentIntentBased() {
      return this.$store.state.account?.isPaymentIntentBased === true;
    },
    dunningPaymentIntent() {
      return this.$store.state.account?.dunning?.paymentIntent;
    },
    dunningInvoice() {
      // For PaymentIntent-based dunning, there is no invoice
      if (this.isPaymentIntentBased) {
        return null;
      }
      const dunningInvoiceId = this.$store.state.account.dunning?.invoice?.invoiceId;
      if (!dunningInvoiceId) {
        return this.$store.state.account.dunning?.invoice;
      }
      const dunningInvoice = this.$store.state.account.invoices?.find((invoice) => invoice.invoice === dunningInvoiceId);
      return dunningInvoice || this.$store.state.account.dunning.invoice;
    },
    alreadyPaid() {
      if (this.isPaymentIntentBased) {
        return this.dunningPaymentIntent?.status === 'succeeded';
      }
      return this.dunningInvoice?.status === 'paid';
    },
    isPendingCapture() {
      // Manual capture: payment method confirmed, funds authorized, platform captures later
      if (this.isPaymentIntentBased) {
        return this.dunningPaymentIntent?.status === 'requires_capture';
      }
      return false;
    },
    paymentComplete() {
      // True when the customer has nothing left to do (hide the form)
      return this.alreadyPaid || this.isPendingCapture;
    },
    alreadyVoid() {
      // PaymentIntents don't have a void status
      if (this.isPaymentIntentBased) {
        return false;
      }
      return this.dunningInvoice?.status === 'void';
    },
    alreadyUncollectible() {
      // PaymentIntents don't have an uncollectible status
      if (this.isPaymentIntentBased) {
        return false;
      }
      return this.dunningInvoice?.status === 'uncollectible';
    },
    treatUncollectibleAsVoid() {
      return ['acct_1GXbpLEWcGtkD3za'].includes(this.$store.state.account.stripeAccountId);
    },
    pastCollectionPeriod() {
      // PaymentIntents don't have a collection period in the same way
      if (this.isPaymentIntentBased) {
        return false;
      }
      const cutoff = this.$store.state.account?.providerSettings?.pastCollectionPeriod || 30;
      if (this.dunningInvoice) {
        const invoiceDate = new Date(this.dunningInvoice.created);
        // check if within 30 days of today
        const today = new Date();
        const thirtyDaysAgo = new Date(today.setDate(today.getDate() - cutoff));
        return invoiceDate < thirtyDaysAgo;
      }
      return false;
    },
    isCanceled() {
      if (this.isPaymentIntentBased) {
        return this.dunningPaymentIntent?.status === 'canceled';
      }
      return false;
    },
    isProcessing() {
      if (this.isPaymentIntentBased) {
        return this.dunningPaymentIntent?.status === 'processing';
      }
      return false;
    },
    isRequiresConfirmation() {
      if (this.isPaymentIntentBased) {
        return this.dunningPaymentIntent?.status === 'requires_confirmation';
      }
      return false;
    },
    requiresAction() {
      return (
        this.isPaymentIntentBased &&
        this.dunningPaymentIntent?.status === 'requires_action' &&
        !this.useAlternatePaymentMethod &&
        !this.cardUpdateComplete &&
        !this.cardUpdateFailed
      );
    },
    pendingCard() {
      return this.dunningPaymentIntent?.card || null;
    },
    pendingClientSecret() {
      return this.dunningPaymentIntent?.clientSecret || null;
    },
    invoiceCard() {
      return this.dunningInvoice?.charge?.payment_method_details?.card || null;
    },
    // 3DS / SCA on a subscription invoice: the card on file just needs authentication. The
    // backend charges our own PaymentIntent (attached to the invoice) and the challenge runs
    // in-app — see verifyInvoicePayment.
    invoiceRequiresVerification() {
      return (
        !this.isPaymentIntentBased &&
        this.dunningInvoice?.paymentIntent?.status === 'requires_action' &&
        !this.useAlternatePaymentMethod &&
        !this.cardUpdateComplete &&
        !this.cardUpdateFailed
      );
    },
    showForm() {
      return (
        !this.requiresAction &&
        !this.invoiceRequiresVerification &&
        !this.cardUpdateFailed &&
        !this.cardUpdateComplete &&
        !this.paymentComplete &&
        !this.alreadyVoid &&
        !this.pastCollectionPeriod &&
        !(this.alreadyUncollectible && this.treatUncollectibleAsVoid) &&
        !this.isCanceled &&
        !this.isProcessing &&
        !this.isRequiresConfirmation
      );
    },
    cardUpdateSuccess() {
      return this.cardUpdateComplete && !this.cardUpdateFailed;
    },
    showWatermark() {
      return this.$store.state.account.branding?.showWatermark && this.cardUpdateSuccess && !this.invoiceNotPayable;
    },
    companyName() {
      return this.$store.state.account.orgName;
    },
    offers() {
      return this.$store.state.account.offers;
    },
    futureDiscount() {
      if (!this.offers || this.offers.length === 0) return null;
      return this.offers.find((o) => o.offerType === 'DISCOUNT');
    },
    invoiceDiscount() {
      if (!this.offers || this.offers.length === 0) return null;
      return this.offers.find((o) => o.offerType === 'INVOICE_DISCOUNT');
    },
    redirectLink() {
      let link = this.$store.state.account?.dunning?.checkoutPage?.redirectLink;
      // prepend https:// if not already there
      if (link && link.indexOf('://') === -1) {
        link = `https://${link}`;
      }
      return link;
    },
  },
  data() {
    const testMode = this.$store.state.account.mode === 'test' || this.$store.state.account.mode === 'sandbox';
    const stripeTestKey = process.env.VUE_APP_STRIPE_TEST_KEY;
    const stripeLiveKey = process.env.VUE_APP_STRIPE_KEY;
    const pkKey = testMode ? stripeTestKey : stripeLiveKey;
    return {
      stripeLoaded: false,
      intentLoaded: false,
      elementsLoaded: false,
      cardUpdateComplete: false,
      cardUpdateFailed: false,
      // The backend reported the invoice can no longer be paid (notPayable). Suppresses the
      // "Subscription Verified" watermark, which would contradict the no-charge copy.
      invoiceNotPayable: false,
      isUpdating: false,
      message: {
        header: '',
        description: '',
      },
      pk_key: pkKey,
      instanceOptions: { locale: 'auto', stripeAccount: null },
      elementsOptions: { locale: 'auto', loader: 'auto', appearance },
      useAlternatePaymentMethod: false,
      verificationInProgress: false,
    };
  },

  mounted() {
    // Load Data for Card Form
    this.instanceOptions.stripeAccount = this.$store.state.account.stripeAccountId;

    // If the PI requires action (e.g. pending 3DS), show verification options
    // instead of fetching a SetupIntent for the Payment Element form. Same for an invoice
    // pending verification — its screen doesn't need a SetupIntent either.
    if (this.isPaymentIntentBased && this.dunningPaymentIntent?.status === 'requires_action' && this.dunningPaymentIntent?.clientSecret) {
      this.intentLoaded = true;
    } else if (this.invoiceRequiresVerification) {
      this.intentLoaded = true;
    } else {
      this.getSetupIntent();
    }
  },

  methods: {
    async retry() {
      // Adding the component back in
      this.cardUpdateFailed = false;
      this.useAlternatePaymentMethod = false;

      // Re-sync account data in case the PI status has changed
      await this.$store.dispatch('SYNC_ACCOUNT', { force: true });

      // If the PI is still requires_action and has a clientSecret, show verification options
      if (this.isPaymentIntentBased && this.dunningPaymentIntent?.status === 'requires_action' && this.dunningPaymentIntent?.clientSecret) {
        this.intentLoaded = true;
        return;
      }

      // Invoice still pending verification — back to its verify screen, no SetupIntent needed
      if (this.invoiceRequiresVerification) {
        this.intentLoaded = true;
        return;
      }

      // Otherwise fall through to the normal form
      this.intentLoaded = false;
      await this.getSetupIntent();
    },

    async completeVerification() {
      if (this.verificationInProgress) return;
      this.verificationInProgress = true;

      try {
        // Create a standalone Stripe instance (Payment Element may not be mounted)
        const stripeKey = this.pk_key;
        const stripeAccountId = this.$store.state.account.stripeAccountId;
        const stripe = window.Stripe(stripeKey, { stripeAccount: stripeAccountId });

        const { error, paymentIntent } = await stripe.handleNextAction({
          clientSecret: this.pendingClientSecret,
        });

        // If user switched to alternate payment method while 3DS was pending, bail out
        if (this.useAlternatePaymentMethod) return;

        if (error) {
          this.cardUpdateFailed = true;
          this.message.header = error.message || this.$t('result.verificationFailed');
          this.message.description = this.$t('result.checkMethod');
          return;
        }

        if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture' || paymentIntent.status === 'processing')) {
          this.cardUpdateComplete = true;
          if (paymentIntent.status === 'requires_capture' || paymentIntent.status === 'processing') {
            this.message.header = this.$t('result.allGood');
            this.message.description = this.$t('result.methodUpdated');
          } else {
            this.message.header = this.$t('result.allGood');
            this.message.description = this.$t('result.charged');
          }
          EventBus.$emit('invoicePaid', { status: paymentIntent.status });
        } else {
          // PI did not succeed (e.g. customer canceled 3DS) — let them try again
          this.cardUpdateFailed = true;
          this.message.header = this.$t('result.verificationIncomplete');
          this.message.description = this.$t('result.tryAgainOrDifferent');
        }
      } catch (err) {
        console.log('[PI-DUNNING] completeVerification error:', err);
        this.cardUpdateFailed = true;
        this.message.header = this.$t('result.verificationError');
        this.message.description = this.$t('result.tryAgainOrDifferent');
      } finally {
        this.verificationInProgress = false;
      }
    },

    async switchToAlternatePayment() {
      trackPageEvent('Recovery Alternate Payment Chosen');
      this.useAlternatePaymentMethod = true;
      this.verificationInProgress = false;
      this.intentLoaded = false;
      await this.getSetupIntent();
    },
    redirect() {
      // navigate to the org's website
      window.location.href = this.redirectLink;
    },
    // 3DS on a subscription invoice: charge the card on file via our own PaymentIntent (attached
    // to the invoice) and complete the challenge in-app — no card re-entry, no redirect.
    async verifyInvoicePayment() {
      if (this.verificationInProgress) return;
      this.verificationInProgress = true;

      // Set once we have client-side confirmation that the charge's PaymentIntent succeeded, so
      // the finalize step returning the invoice before Stripe has flipped it to 'paid' still
      // reports success.
      let paymentConfirmed = false;
      try {
        let data = await OrgService.chargeBalanceToCard({});
        if (data.requiresAction && data.clientSecret) {
          // Standalone Stripe instance — the Payment Element is not mounted in this state.
          // A native invoice PaymentIntent (org supplied recovery keys) can only be confirmed with
          // the merchant's OWN publishable key — the platform key, even scoped with stripeAccount,
          // can't act on an account-owned PaymentIntent.
          const stripe =
            data.native && data.publishableKey
              ? window.Stripe(data.publishableKey)
              : window.Stripe(this.pk_key, { stripeAccount: this.$store.state.account.stripeAccountId });
          const { error, paymentIntent } = await stripe.handleNextAction({ clientSecret: data.clientSecret });
          if (error) {
            this.cardUpdateFailed = true;
            this.message.header = error.message || this.$t('result.verificationFailed');
            this.message.description = this.$t('result.checkMethod');
            return;
          }
          if (!paymentIntent || paymentIntent.status !== 'succeeded') {
            // e.g. customer canceled the 3DS challenge — let them try again
            this.cardUpdateFailed = true;
            this.message.header = this.$t('result.verificationIncomplete');
            this.message.description = this.$t('result.tryAgainOrDifferent');
            return;
          }
          // 3DS succeeded → the charge went through regardless of the finalize call's outcome.
          paymentConfirmed = true;
          try {
            // Reassign so invoicePaid carries the paid invoice (markInvoicePaid reads
            // .invoice/.status to flip the badge in the list)
            data = await OrgService.confirmInvoicePaymentIntent({ paymentIntentId: data.paymentIntentId });
          } catch (confirmError) {
            // The charge already succeeded and Stripe credits the attached payment itself — a
            // finalization hiccup must not surface as a payment error.
            console.log('[INVOICE-3DS] confirm after successful 3DS failed:', confirmError);
          }
        }
        if (data.noOutstandingBalance) {
          // Nothing left to charge (e.g. already resolved before we got to it) — there's no
          // invoiceId to patch a specific invoice with, so re-sync the whole account instead.
          // Re-sync before showing success so an auth/network failure here doesn't surface
          // alongside a "You're all good" message that's no longer backed by fresh state.
          await this.$store.dispatch('SYNC_ACCOUNT', { force: true });
        }
        this.cardUpdateComplete = true;
        if (data.noOutstandingBalance) {
          this.message.header = this.$t('result.allGood');
          this.message.description = this.$t('result.upToDate');
        } else if (data.notPayable && !paymentConfirmed) {
          // The invoice was voided or deleted between page load and submit, so the backend
          // explicitly reports it can no longer be paid. This is the ONLY shape we claim "no charge
          // was made" for. Any other non-success status is unconfirmed, not a known failure: on the
          // attached-PaymentIntent path the invoice can still read 'open' with amountPaid 0 for a
          // charge that DID collect (issuer skipped the challenge, so paymentConfirmed is false
          // too), and telling that customer their card was not charged would be false. Those keep
          // master's copy below, as does an old backend that sends no notPayable at all.
          // The !paymentConfirmed guard: on the confirm route notPayable only comes back after the
          // PaymentIntent SUCCEEDED (the API logs "collected N against void invoice — reconcile
          // manually"), so a confirmed 3DS charge must fall through to the charged copy below,
          // which is true, rather than claim no charge was made. Durable fix: manual capture
          // (XDEV-2662).
          this.invoiceNotPayable = true;
          this.setInvoiceNotPayableMessage();
          // Refresh the account so the invoice list reflects the void status instead of a stale
          // "Failed" badge. Cosmetic: a sync failure must not replace the accurate copy above.
          try {
            await this.$store.dispatch('SYNC_ACCOUNT', { force: true });
          } catch (syncErr) {
            console.log('[INVOICE] account sync after notPayable failed:', syncErr);
          }
        } else {
          // Copy is optimistic (as in master), but only a confirmed collection flips the invoice
          // badge via invoicePaid.
          const chargeCollected =
            paymentConfirmed || data.chargeCollected === true || this.isPaymentSuccessStatus(data.status);
          this.message.header = this.$t('result.allGood');
          this.message.description = this.$t('result.charged');
          if (chargeCollected) EventBus.$emit('invoicePaid', data);
        }
      } catch (err) {
        console.log('[INVOICE-3DS] verifyInvoicePayment error:', err);
        this.cardUpdateFailed = true;
        this.message.header = this.$t('result.verificationError');
        this.message.description = this.$t('result.tryAgainOrDifferent');
      } finally {
        this.verificationInProgress = false;
      }
    },
    isLoaded() {
      this.elementsLoaded = true;
    },

    // Statuses that mean the outstanding balance was genuinely collected. (Invoice: 'paid'.
    // PaymentIntent: 'succeeded', 'requires_capture' for manual capture, 'processing' for async
    // methods like bank debits.) Used to gate the invoicePaid emit — NOT to decide the copy,
    // because anything outside this set is merely unconfirmed, not a known failure.
    isPaymentSuccessStatus(status) {
      return ['paid', 'succeeded', 'requires_capture', 'processing'].includes(status);
    },

    // The card was saved, but the invoice can no longer be paid: it was voided or deleted between
    // page load and submit, so the backend returned { notPayable: true, paymentMethodSaved: true }.
    // (An uncollectible invoice is still payable and never lands here.) Mirrors the alreadyVoid
    // copy. Only for the updateCard flow, where a card really was saved.
    setCardSavedNoChargeMessage() {
      this.message.header = this.$t('result.cardSaved');
      this.message.description = this.$t('result.cardSavedNotPayable');
    },

    // Same notPayable signal, but from verifyInvoicePayment: that flow charges the card already on
    // file (no paymentMethodId, no Payment Element), so there is no saved card to mention.
    setInvoiceNotPayableMessage() {
      this.message.header = this.$t('result.invoiceNotPayable');
      this.message.description = this.$t('result.noCharge');
    },

    // Configures Stripe by setting up the elements and creating the card element.
    async getSetupIntent() {
      try {
        const data = await OrgService.getSetupIntent();
        this.elementsOptions.clientSecret = data.client_secret;
        this.intentLoaded = true;
      } catch (err) {
        console.log('Error on getSetupIntent:', err.response?.data);
        this.$store.commit('SET_ERROR', { errorMessage: err.response?.data });
        // router.push('/error');
      }
    },

    async updateCard() {
      if (this.isUpdating) return; // Disable button while loading!
      trackPageEvent('Recovery Pay Clicked');
      this.isUpdating = true;
      const { elements, instance } = this.$refs.elms;

      /*
      const previewMode = true;
      if (previewMode) {
        console.log('PREVIEW MODE: No card update or invoice payment will be processed.');
        await new Promise((resolve) => setTimeout(resolve, 1000));
        this.cardUpdateComplete = true;
        this.message.header = `You're all good.`;
        this.message.description = `We were able to charge your card on file. There's nothing you need to do for your account at this time.`;
        EventBus.$emit('invoicePaid');
      }
      */

      try {
        const response = await instance.confirmSetup({ elements, redirect: 'if_required' });
        if (response.error) {
          // confirmSetup talks to Stripe directly, so this failure never reaches our API.
          trackPageEvent('Recovery Card Setup Failed', { code: response.error.code, declineCode: response.error.decline_code });
          // Inform the customer that there was an error.
          this.cardUpdateFailed = true;
          this.message.header = response.error.message || this.$t('result.paymentError');
          this.message.description = this.$t('result.checkMethod');
        }
        if (response.setupIntent && response.setupIntent.status === 'succeeded') {
          // Set new payment method as default and charge for outstanding balance
          try {
            let data;
            // Set once we have client-side confirmation that an invoice charge's PaymentIntent
            // succeeded (3DS), so a finalize call returning the invoice before Stripe has flipped
            // it to 'paid' still reports success.
            let paymentConfirmed = false;
            if (this.isPaymentIntentBased) {
              // PaymentIntent-based dunning (one-off payments)
              data = await OrgService.chargePaymentIntent({ paymentMethodId: response.setupIntent.payment_method });
              console.log('[PI-DUNNING] chargePaymentIntent result:', data.status, data);

              // If the PI requires customer action (e.g. 3DS for the actual payment),
              // handle it client-side via Stripe.js
              if (data.status === 'requires_action' && data.clientSecret) {
                console.log('[PI-DUNNING] Handling next action (3DS)...');
                const nextAction = await instance.handleNextAction({ clientSecret: data.clientSecret });
                console.log('[PI-DUNNING] handleNextAction result:', nextAction);

                if (nextAction.error) {
                  console.log('[PI-DUNNING] 3DS challenge failed:', nextAction.error.message);
                  trackPageEvent('Recovery 3DS Failed', { code: nextAction.error.code });
                  this.cardUpdateFailed = true;
                  this.message.header = nextAction.error.message || this.$t('result.authFailed');
                  this.message.description = this.$t('result.checkMethod');
                  this.isUpdating = false;
                  return;
                }

                // After successful 3DS, the PI status should have progressed
                if (nextAction.paymentIntent) {
                  console.log('[PI-DUNNING] PI status after 3DS:', nextAction.paymentIntent.status);
                  data = { ...data, status: nextAction.paymentIntent.status };
                }
              }
            } else {
              // Invoice-based dunning (subscriptions). setupIntentId is forwarded so the backend
              // can stamp the campaign's surfaceActivity (assisted-recovery attribution).
              data = await OrgService.chargeBalanceToCard({
                paymentMethodId: response.setupIntent.payment_method,
                setupIntentId: response.setupIntent.id,
              });
              console.log('Invoice data', data);

              // 3DS: the backend returned a client_secret — complete the challenge in-app, then
              // finalize. Usually it's our own PaymentIntent (attached to the invoice); when the
              // org supplied recovery keys it's the invoice's own native PaymentIntent, which only
              // the merchant's OWN publishable key can act on (the mounted instance uses the
              // platform key + stripeAccount and can't).
              if (data.requiresAction && data.clientSecret) {
                const stripeForAction = data.native && data.publishableKey ? window.Stripe(data.publishableKey) : instance;
                const nextAction = await stripeForAction.handleNextAction({ clientSecret: data.clientSecret });
                if (nextAction.error) {
                  trackPageEvent('Recovery 3DS Failed', { code: nextAction.error.code });
                  this.cardUpdateFailed = true;
                  this.message.header = nextAction.error.message || this.$t('result.authFailed');
                  this.message.description = this.$t('result.checkMethod');
                  this.isUpdating = false;
                  return;
                }
                if (!nextAction.paymentIntent || nextAction.paymentIntent.status !== 'succeeded') {
                  // e.g. customer canceled the 3DS challenge — let them try again
                  this.cardUpdateFailed = true;
                  this.message.header = this.$t('result.verificationIncomplete');
                  this.message.description = this.$t('result.tryAgainOrDifferent');
                  this.isUpdating = false;
                  return;
                }
                // 3DS succeeded → the charge went through regardless of the finalize call's outcome.
                paymentConfirmed = true;
                try {
                  data = await OrgService.confirmInvoicePaymentIntent({
                    paymentIntentId: data.paymentIntentId,
                    setupIntentId: response.setupIntent.id,
                  });
                } catch (confirmError) {
                  // The charge already succeeded and Stripe credits the attached payment itself —
                  // a finalization hiccup must not surface as a payment error.
                  console.log('[INVOICE-3DS] confirm after successful 3DS failed:', confirmError);
                }
              }
            }

            if (data.noOutstandingBalance) {
              // Nothing left to charge (e.g. already resolved before we got to it) — there's no
              // invoiceId to patch a specific invoice with, so re-sync the whole account instead.
              // Re-sync before showing success so an auth/network failure here doesn't surface
              // alongside a "You're all good" message that's no longer backed by fresh state.
              await this.$store.dispatch('SYNC_ACCOUNT', { force: true });
            }

            this.cardUpdateComplete = true;

            if (data.noOutstandingBalance) {
              this.message.header = this.$t('result.allGood');
              this.message.description = this.$t('result.upToDate');
            } else if (data.notPayable && !paymentConfirmed) {
              // The invoice was voided or deleted between page load and submit, so the backend
              // explicitly reports it can no longer be paid: the card was saved, nothing charged.
              // This is the ONLY shape we claim "your card was not charged" for. Any other
              // non-success status is unconfirmed, not a known failure: on the attached-
              // PaymentIntent path the invoice can still read 'open' with amountPaid 0 for a charge
              // that DID collect (issuer skipped the challenge, so paymentConfirmed is false too),
              // and that claim would be false there. Those keep master's copy below, as does an old
              // backend that sends no notPayable at all. Never reached for PaymentIntent-based
              // dunning, which has no invoice and never carries notPayable.
              // The !paymentConfirmed guard: on the confirm route notPayable only comes back after
              // the PaymentIntent SUCCEEDED, so a confirmed 3DS charge must fall through to the
              // charged copy below, which is true, rather than claim the card was not charged.
              // Durable fix: manual capture (XDEV-2662).
              this.invoiceNotPayable = true;
              this.setCardSavedNoChargeMessage();
              // Refresh the account so the invoice list reflects the void status instead of a
              // stale "Failed" badge. Cosmetic: a sync failure must not replace the copy above.
              try {
                await this.$store.dispatch('SYNC_ACCOUNT', { force: true });
              } catch (syncErr) {
                console.log('[INVOICE] account sync after notPayable failed:', syncErr);
              }
            } else {
              // Copy is optimistic (as in master), but only a confirmed collection flips the
              // invoice badge via invoicePaid.
              // Invoice: 'paid'. PaymentIntent: 'succeeded', 'requires_capture', or 'processing'.
              const chargeCollected =
                paymentConfirmed || data.chargeCollected === true || this.isPaymentSuccessStatus(data.status);

              this.message.header = this.$t('result.allGood');
              // Different message based on capture status
              // requires_capture = manual capture (funds authorized but not captured)
              // processing = async payment method (e.g. bank debit), settling
              // succeeded/paid = automatic capture (funds captured)
              if (data.status === 'requires_capture' || data.status === 'processing') {
                this.message.description = this.$t('result.methodUpdated');
              } else {
                this.message.description = this.$t('result.charged');
              }
              if (chargeCollected) EventBus.$emit('invoicePaid', data);
            }
          } catch (error) {
            EventBus.$emit('logIssue', this.$store.state.account);
            console.log('Error updating card and processing payment', error);
            this.cardUpdateFailed = true;
            this.message.header = response.error?.message || this.$t('result.paymentError');
            this.message.description = this.$t('result.checkMethod');
          }
        }
      } catch (error) {
        EventBus.$emit('logIssue', this.$store.state.account);
        console.log('error', error);
      } finally {
        this.isUpdating = false;
      }
    },
  },
  filters: {
    formatCouponDuration: Filters.formatCouponDuration,
    capitalize(value) {
      if (!value) return '';
      const str = String(value);
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
  },
};
</script>
<style scoped>
@media only screen and (min-width: 768px) and (max-width: 1000px) {
  .card-form__inner {
    padding-left: 8px;
    padding-right: 8px;
    margin-left: 2rem;
    margin-right: 2rem;
  }
  .card-form__button {
    width: 80%;
    margin-left: auto;
    margin-right: auto;
  }
}
.card-form__inner {
  padding-top: 40px;
  min-height: 350px;
}

.payment-message {
  padding-top: 40px;
  min-height: 300px;
}

@media only screen and (max-width: 480px) {
  .card-form__inner {
    /* Extra space above the card inputs for small screens */
    padding-top: 1rem;
    margin-left: 0;
    margin-right: 0;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    min-height: 350px;
  }
}
</style>
